import React from 'react';
import moment from 'moment';
import { useSelector } from 'react-redux';
import Alert from '../../../components/alert/alert';
import useSquareConnectInfo from '../../../components/menu/square-shared/use-square-connect-info';
import { CONNECT_STATUS_ROLES } from '../../../components/layout/layout';
import { isConnectDirect, isConnectPending } from '../../../utils/connectModel';
import StripeNotices from './stripe-notices';

// Who can open Business Details, where a payout account is changed.
const PAYOUT_DETAILS_ROLES = [150, 200];

const DashboardAlerts = ({
  userRoles,
  globalCompany,
  bankAccount,
  storeList,
  storeWithMissingSettings,
}) => {
  const { needsReconnect } = useSquareConnectInfo();
  // Read once per company by layout.js (fetchConnectStatus) and served from
  // the store from then on — this alert must never trigger its own request.
  const connectStatus = useSelector(
    (state) => state.companyReducer.connectStatus
  );
  // The company that status belongs to: the store only writes a company's
  // answer under its own id (companySlice.js). setGlobalCompany leaves
  // connectStatus alone, though; layout.js's fetchConnectStatus.pending
  // clears it one commit after a selection change that did not reload. For
  // that one commit the gate below could read the previous company's
  // hasAccount. Nothing crosses companies: the session is minted for the
  // selected company (StripeNotices keys its instance on it), so the worst
  // case is one refused or one extra mint.
  const selectedCompanyId = useSelector(
    (state) => state.companyReducer.selected?.id
  );
  const isOwner = !userRoles.includes(100);
  if (!isOwner) return null;

  const trialExpired =
    !globalCompany.subscriptionid &&
    globalCompany.trialend &&
    globalCompany.trialend < moment().format();

  // A Stripe account exists but has not finished onboarding. Purely
  // informational for a legacy company: its payments keep working exactly as
  // they do today, and going live on Connect is a separate admin decision.
  // A company born Pending gets layout.js's band instead, on every page, so
  // this banner is never shown to it as well (spec 2026-09-25 §5.5).
  //
  // Gated on CONNECT_STATUS_ROLES — the exact roles the sidebar's own
  // Payments entry is gated on (layout.js's dashboardMenu) — rather than
  // just "not the platform admin": 250 and 300 pass that looser check but
  // cannot open the page this alert's button points at.
  const canOpenPayments = userRoles.some((role) =>
    CONNECT_STATUS_ROLES.includes(role)
  );
  // Stripe's own action-required box (Finance > Overview draws the same
  // one), for exactly the people Overview shows it to: not the admin (above),
  // a role that can open Finance, and a company that has a Stripe account —
  // read from the store, never from a request of our own. No paymentModel
  // gate: Overview mints for a legacy company still onboarding as well.
  // Anyone else gets a null company id, and nothing is minted for them.
  const stripeNoticesCompanyId =
    canOpenPayments && !!connectStatus?.hasAccount && selectedCompanyId
      ? selectedCompanyId
      : null;
  const stripeSetupIncomplete =
    canOpenPayments &&
    !!connectStatus?.hasAccount &&
    connectStatus?.connectStatus !== 'READY' &&
    !isConnectPending(connectStatus);

  // Owner, 2026-10-06: a payout bounced off the restaurant's bank or card.
  // Stripe marks that account `errored` and stops scheduled payouts until it
  // is changed; its own banner says so on Finance, and until now the
  // Dashboard said nothing. The account itself stays READY and
  // payouts-enabled, so this is its own fact (payoutDestinationStatus) and
  // not one of the two above.
  //
  // Only for a company Stripe actually pays: one moved back to the legacy
  // model keeps its Stripe account and whatever status its bank had there,
  // and its payouts no longer come that way.
  //
  // Shown to everyone who reads the money. The button is only for the roles
  // that can open Business Details, where the bank is changed (router.js
  // gates it to 150 and 200): the accountant reads the same sentence without
  // a button that would lead nowhere. The same holds for Finish Verification
  // below, which goes to the same page.
  const payoutsOnHold =
    canOpenPayments &&
    isConnectDirect(connectStatus) &&
    !!connectStatus?.hasAccount &&
    connectStatus?.payoutDestinationStatus === 'errored';
  const canChangeBank = userRoles.some((role) => PAYOUT_DETAILS_ROLES.includes(role));

  // Owner, 2026-09-25: the checking-account banner is for a company Mercury
  // pays. One with a Stripe account, or born Pending, gets paid by Stripe.
  const headedForConnect =
    !!connectStatus?.hasAccount || isConnectPending(connectStatus);

  const linkedStore = storeWithMissingSettings
    ? storeList.find((s) => s.id === storeWithMissingSettings.id)
    : null;
  const hasMissingSettings =
    linkedStore &&
    (linkedStore.tax == null || linkedStore.phone == null || linkedStore.phone === '');

  return (
    // A flex column with a gap, not space-y: Stripe's box is the first item
    // and takes no slot when it has nothing to show, which space-y (a margin
    // on every later sibling) cannot do.
    <div className="relative flex flex-col gap-2.5">
      {stripeNoticesCompanyId && (
        <StripeNotices
          key={stripeNoticesCompanyId}
          companyId={stripeNoticesCompanyId}
        />
      )}
      {payoutsOnHold && (
        <Alert
          type="error"
          description="Payouts are on hold: Stripe couldn't deliver a payout to your payout account. Update your payout details to resume them."
          buttonText={canChangeBank ? 'Update payout details' : undefined}
          navigate="/dashboard/finance/business-details#payout-details"
        />
      )}
      {needsReconnect && (
        <Alert
          type="warning"
          description="Square connection needs to be renewed — new permissions are required. Menu, price and stock sync may be paused."
          buttonText="Reconnect"
          navigate="/dashboard/settings/integrations"
        />
      )}
      {!globalCompany.subscriptionid && (
        <Alert
          type="info"
          description="Please upgrade your account by selecting a subscription plan that meets your expectations."
          buttonText="Upgrade Now"
          navigate="/dashboard/settings/subscription"
        />
      )}
      {bankAccount.length === 0 && !headedForConnect && (
        <Alert
          type="info"
          description="Please add your checking account for your store's payouts."
          buttonText="Add Now"
          navigate="/dashboard/settings/bank-accounts"
        />
      )}
      {hasMissingSettings && (
        <Alert
          type="info"
          description={`Please update your ${storeWithMissingSettings.name}'s Store Settings (Sales Tax, Store Phone Number, etc.)`}
          buttonText="Store Settings"
          navigate={`/dashboard/stores/${storeWithMissingSettings.id}/settings`}
        />
      )}
      {trialExpired && (
        <Alert
          type="error"
          description="Your trial has expired. Please upgrade to a paid subscription to continue using our services and access all of our premium features. Thank you"
          buttonText="Upgrade Now"
          navigate="/dashboard/settings/subscription"
        />
      )}
      {stripeSetupIncomplete && (
        <Alert
          type="info"
          description="Stripe still needs a few details to verify your business — worth finishing now before it slips your mind."
          buttonText={canChangeBank ? 'Finish Verification' : undefined}
          // Straight to the page, not through the Settings -> Payments
          // redirect it used to take: that route is now only a Navigate to
          // this same destination (router.js), and the banner's whole job is
          // to shorten the path to the form.
          navigate="/dashboard/finance/business-details"
        />
      )}
    </div>
  );
};

export default DashboardAlerts;
